import classNames from 'classnames';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useRef } from 'react';
import styled from 'styled-components';

import { SBHorizontal, Vertical } from 'atoms/layout/flex';
import { WithScrollButtons } from 'atoms/navigation/WithScrollButtons';
import { hideScrollbar } from 'style/utils';

import type { LineupStyle } from './types';

const Root = styled(Vertical).attrs({ as: 'article' })`
  height: 100%;
  justify-content: space-between;
  position: relative;
  isolation: isolate;
`;
const Main = styled(Vertical)`
  .forced_inline & {
    ${hideScrollbar}
    overflow: auto;
    overflow-y: clip;
  }
`;
const Top = styled(Vertical)`
  --header-height: calc(6 * var(--unit) + var(--half-unit));
  --gap: 0px;
  gap: var(--gap);
  min-height: calc(var(--header-height) + var(--gap));
  &.bannerOnly {
    min-height: 0;
  }
  .forced_inline & {
    position: sticky;
    left: 0;
    top: 0;
  }
`;
const Banner = styled.div`
  min-height: var(--triple-unit);
`;
const Header = styled(SBHorizontal)`
  min-height: var(--header-height);
  padding: 0 var(--unit) var(--half-unit);

  &:first-child {
    padding: var(--unit) var(--unit) var(--half-unit);
  }
`;
const Center = styled.div`
  .inline & {
    padding: 0 var(--half-unit);
  }
  .spread & {
    padding: 0 var(--intermediate-unit);
  }
`;
const Bottom = styled(Vertical)`
  --footer-height: calc(11 * var(--unit));
  --gap: var(--unit);
  --padding-bottom: var(--unit);
  gap: var(--gap);
  min-height: calc(
    var(--custom-footer-height, var(--footer-height)) + var(--gap) +
      var(--padding-bottom)
  );
  padding-bottom: var(--padding-bottom);
  justify-content: flex-end;

  &.withButtons {
    --footer-height: calc(10 * var(--unit));
  }
  &.withButtons:not(.withSubFooter) {
    --padding-bottom: 0;
  }
`;
const Footer = styled(Vertical)`
  min-height: var(--custom-footer-height, var(--footer-height));
  flex: 1;
  padding: 0 var(--unit);
  gap: 2px;
`;
const SubFooter = styled.div`
  display: contents;
`;

type Props = {
  lineupStyle: LineupStyle;
  withButtons?: boolean;
  withSubFooter?: boolean;
  children: ReactNode;
  banner?: ReactNode;
  header?: ReactNode;
  footer: ReactNode;
  subFooter?: ReactNode;
};

export const ContenderLayout: FC<PropsWithChildren<Props>> = ({
  lineupStyle,
  withButtons = false,
  withSubFooter = false,
  children,
  banner,
  header,
  footer,
  subFooter,
}: Props) => {
  const bannerOnly = !!banner && !header;
  const wrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);

  return (
    <Root className={classNames(lineupStyle)}>
      <WithScrollButtons
        wrapperRef={wrapperRef}
        scrollContainerRef={scrollContainerRef}
        contained
      >
        <Main gap={bannerOnly ? 1 : 0.5} ref={scrollContainerRef}>
          <Top className={classNames({ bannerOnly })}>
            {!!banner && <Banner>{banner}</Banner>}
            {!!header && <Header>{header}</Header>}
          </Top>
          <Center>{children}</Center>
        </Main>
      </WithScrollButtons>
      <Bottom className={classNames({ withButtons, withSubFooter })}>
        <Footer>{footer}</Footer>
        {!!subFooter && <SubFooter>{subFooter}</SubFooter>}
      </Bottom>
    </Root>
  );
};
